import { CLIENT_CHART_CROSS_FILTER, type ClientChartCrossFilterSpec, type DashboardTile } from './dvm-layout-builder.types';

/**
 * Pure helpers for cross-filtering between dashboard tiles: which tiles can
 * emit / react, the client rows of a client-rendered chart, and the row
 * filtering itself. Shared by the builder shell and its layout-JSON export.
 */

/** Client-rendered chart families answer cross-filters by filtering their own rows; everything else is server-driven. */
export function crossFilterSpec(tile: DashboardTile): ClientChartCrossFilterSpec | null {
  return CLIENT_CHART_CROSS_FILTER[tile.layoutType] ?? null;
}

export function isCrossFilterTile(tile: DashboardTile): boolean {
  const spec = crossFilterSpec(tile);
  return !!spec && !!tile[spec.dataField] && Array.isArray(tile[spec.rowsField]);
}

/** Waterfalls re-fetch from the compute API instead of filtering client rows. */
export function isRecomputeCrossFilterTile(tile: DashboardTile): boolean {
  return tile.layoutType === 'WATERFALL' && !!tile.waterfallBars && !!tile.payload;
}

export function canConfigureCrossFilter(tile: DashboardTile): boolean {
  return isCrossFilterTile(tile) || (tile.layoutType === 'WATERFALL' && !!tile.waterfallData);
}

/** Input controls and popups are not "charts" for recompute / cross-filter purposes. */
export function isChartTile(tile: DashboardTile): boolean {
  return tile.layoutType !== 'SLIDER' && tile.layoutType !== 'INPUT_BOX' && tile.layoutType !== 'DROPDOWN' && tile.layoutType !== 'FILE_UPLOAD' && tile.layoutType !== 'POPUP';
}

export function tileClientRows(tile: DashboardTile): Array<Record<string, unknown>> {
  const spec = crossFilterSpec(tile);
  const rows = spec ? tile[spec.rowsField] : undefined;
  return Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : [];
}

export function tileClientData(tile: DashboardTile): Record<string, unknown> | null {
  const spec = crossFilterSpec(tile);
  const data = spec ? tile[spec.dataField] : undefined;
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
}

export function withTileClientRows(tile: DashboardTile, rows: Array<Record<string, unknown>>): DashboardTile {
  const spec = crossFilterSpec(tile);
  const data = tileClientData(tile);
  if (!spec || !data) return tile;
  return { ...tile, [spec.dataField]: { ...data, staticData: rows } } as DashboardTile;
}

/** The column a tile publishes when clicked: its explicit filterKey, else its category column. */
export function effectiveTileFilterKey(tile: DashboardTile): string {
  if (tile.filterKey && tile.filterKey.trim()) return tile.filterKey.trim();
  const data = tileClientData(tile);
  const caps = data?.['capabilities'] as { category?: string } | undefined;
  return caps?.category || '';
}

/** Dataset(s) a tile reads: `payload.datasets` (waterfall) or the single `payload.dataset`. */
export function tileDatasets(tile: DashboardTile): string[] {
  const single = String(tile.payload?.['dataset'] ?? '').trim();
  const multi = Array.isArray(tile.payload?.['datasets'])
    ? (tile.payload['datasets'] as unknown[]).map(String).filter(Boolean)
    : [];
  return multi.length ? multi : single ? [single] : [];
}

/** Tiles that can react to a cross-filter from `sourceTile` (the live preview draft excluded). */
export function reactableTilesFor(sourceTile: DashboardTile, tiles: DashboardTile[], previewTileId: string | null): DashboardTile[] {
  return tiles.filter(
    (t) =>
      t.id !== sourceTile.id &&
      t.id !== previewTileId &&
      (isCrossFilterTile(t) || isRecomputeCrossFilterTile(t)),
  );
}

/** Explicit listener list of a tile, else every reactable tile. */
export function effectiveListenerTileIds(sourceTile: DashboardTile, tiles: DashboardTile[], previewTileId: string | null): string[] {
  if (Array.isArray(sourceTile.listenerTileIds)) return sourceTile.listenerTileIds;
  return reactableTilesFor(sourceTile, tiles, previewTileId).map((t) => t.id);
}

export interface ActiveCrossFilter {
  column: string;
  values: string[];
  sourceTileId: string;
}

/** Filter the client rows of every listening client-rendered tile (or restore them when `filter` is null). */
export function applyCrossFilterToClientTiles(tiles: DashboardTile[], filter: ActiveCrossFilter | null, listeners: Set<string>): DashboardTile[] {
  const filterValues = filter ? new Set(filter.values) : null;
  return tiles.map((tile) => {
    if (!isCrossFilterTile(tile)) return tile;
    if (filter && tile.id === filter.sourceTileId) return tile;
    const rows = tileClientRows(tile);
    const reacts = !!filter && listeners.has(tile.id) && rows.some((row) => filter.column in row);
    const nextRows = reacts && filterValues
      ? rows.filter((row) => filterValues.has(String(row[filter!.column] ?? '')))
      : rows;
    return withTileClientRows(tile, nextRows);
  });
}

/** Apply every active dropdown filter (field ∈ values) to the client rows of the client-rendered tiles that carry that field. */
export function applyDropdownFiltersToClientTiles(tiles: DashboardTile[], filters: Array<{ field: string; values: string[] }>): DashboardTile[] {
  return tiles.map((tile) => {
    if (!isCrossFilterTile(tile)) return tile;
    const original = tileClientRows(tile);
    let rows = original;
    filters.forEach(({ field, values }) => {
      if (!original.some((row) => field in row)) return;
      const wanted = new Set(values.map(String));
      rows = rows.filter((row) => wanted.has(String(row[field] ?? '')));
    });
    return withTileClientRows(tile, rows);
  });
}
